import axios from "axios";
import { AxiosError } from "axios";
import { ServerError, ValidationError, SuccessResult } from "./ServerResponses";

export class ServerResponse {
	static isSuccess<T>(model: any): model is SuccessResult<T> {
		return model.data !== undefined && model.message !== undefined;
	}
	static isError(model: any): model is ServerError {
		return model.message !== undefined && model.statusCode !== undefined;
	}
	static isModelValidation(model: any): model is ValidationError {
		return model.valid !== undefined && model.errors !== undefined;
	}
}


const serializeAxiosError = (errorResponse: AxiosError) => {
	let serverError: ServerError;
	let response = errorResponse.response;

	// Happens if the http request gets killed mid request
	if (!response) {
		serverError = { message: "Received no response", statusCode: 500 };
	} else if (response.status === 404) {
		serverError = { message: "Server is not responding", statusCode: response.status };
	}
	// Redirect to sign in page
	else if (response.status === 401) {
		let path = window.location.pathname;
		let redirect = "?returnUrl=" + encodeURIComponent(path);
		window.location.replace("/sign-in" + redirect);
		let standardServerResponse = response.data as SuccessResult<string>;
		serverError = { message: standardServerResponse.message, statusCode: response.status };
	} else {
		let standardServerResponse = response.data as SuccessResult<string>;
		serverError = { message: standardServerResponse.message, statusCode: response.status };
	}

	return serverError;
};

const uploadProgressPercent = (progress: ProgressEvent) => {
	return Math.round((progress.loaded * 100) / progress.total);
};

const offlineError: ServerError = {
	message: "Maker is offline",
	statusCode: 503,
};

export class OldWebClient {
	static async Get<T>(url: string) {
		if (!navigator.onLine) {
			return offlineError;
		}

		try {
			const response = await axios.get(url, { headers: { Pragma: "no-cache" } });
			return response.data as SuccessResult<T> | ValidationError;
		} catch (errorResult) {
			return serializeAxiosError(errorResult as AxiosError);
		}
	}

	static async GetWithBody<T>(url: string, reqBody: any) {
		if (!navigator.onLine) {
			return offlineError;
		}

		try {
			const response = await axios(url, {
				method: "get",
				params: reqBody,
				headers: { Pragma: "no-cache" },
			});
			return response.data as SuccessResult<T> | ValidationError;
		} catch (errorResult) {
			return serializeAxiosError(errorResult as AxiosError);
		}
	}

	static async GetWithoutModelValidation<T>(url: string) {
		if (!navigator.onLine) {
			return offlineError;
		}

		try {
			const response = await axios.get(url, { headers: { Pragma: "no-cache" } });
			return response.data as SuccessResult<T>;
		} catch (errorResult) {
			return serializeAxiosError(errorResult as AxiosError);
		}
	}

	static async Post<T>(url: string, data: any, headers: any = { Pragma: "no-cache" }) {
		if (!navigator.onLine) {
			return offlineError;
		}

		try {
			const response = await axios(url, {
				method: "post",
				data: data,
				headers: headers,
			});
			return response.data as SuccessResult<T> | ValidationError;
		} catch (errorResult) {
			return serializeAxiosError(errorResult as AxiosError);
		}
	}

	static async Put<T>(url: string, data: any, headers = { Pragma: "no-cache" }) {
		if (!navigator.onLine) {
			return offlineError;
		}

		try {
			const response = await axios(url, {
				method: "put",
				data: data,
				headers: headers,
			});
			return response.data as SuccessResult<T> | ValidationError;
		} catch (errorResult) {
			return serializeAxiosError(errorResult as AxiosError);
		}
	}

	static async Delete<T>(url: string, data?: any) {
		if (!navigator.onLine) {
			return offlineError;
		}

		try {
			const response = await axios(url, {
				method: "delete",
				data: data,
				headers: { Pragma: "no-cache" },
			});
			return response.data as SuccessResult<T> | ValidationError;
		} catch (errorResult) {
			return serializeAxiosError(errorResult as AxiosError);
		}
	}

	static async PostFile<T>(url: string, file: File) {
		if (!navigator.onLine) {
			return offlineError;
		}

		try {
			let formData = new FormData();
			formData.append("file", file, file.name);
			const response = await axios(url, {
				method: "post",
				data: formData,
				headers: { Pragma: "no-cache" },
			});
			return response.data as SuccessResult<T>;
		} catch (errorResult) {
			return serializeAxiosError(errorResult as AxiosError);
		}
	}

	static async PutFile(url: string, file: File, onUploadProgress?: (percent: number) => void) {
		if (!navigator.onLine) {
			return offlineError;
		}

		try {
			await axios.put(url, file, {
				onUploadProgress: onUploadProgress ? (p) => onUploadProgress(uploadProgressPercent(p)) : undefined,
				headers: { Pragma: "no-cache" },
			});
			let response: SuccessResult<boolean> = {
				data: true,
				message: "",
			};
			return response;
		} catch (errorResult) {
			return serializeAxiosError(errorResult as AxiosError);
		}
	}
}
